import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { BACKUP_LINK } from '@/features/backup';

import { type StorageRisk } from './detect';

/*
 * The copy per browser. The fix differs — a Home Screen app on iOS, where every
 * browser is WebKit and switching would change nothing; the Dock or another
 * browser on a Mac — but both lead to the same trap: the new place has its own
 * empty storage. So the move is always said together with how data gets
 * there, which is a backup restored on the other side.
 */
const COPY: Record<StorageRisk, { cause: string; fix: string; how: string; move: string }> = {
  ios: {
    cause: 'iOS browsers delete stored data for a site not visited in 7 days.',
    fix: 'Add to Home Screen and open the app from there.',
    how: 'Share › Add to Home Screen',
    move: 'The Home Screen app has separate storage. Back up here, then restore the backup in the app.',
  },
  'macos-safari': {
    cause: 'Safari deletes stored data for a site not visited in 7 days.',
    fix: 'Add to Dock, or use Chrome, Edge or Firefox.',
    how: 'File › Add to Dock',
    move: 'The Dock app and other browsers have separate storage. Back up here, then restore the backup there.',
  },
};

/**
 * Said on every launch, not once. The deletion is silent and total, and a
 * pilot who closed a one-time notice months ago has no reason to remember it
 * the week before a long break. "Every launch" is every page load: a client-side
 * navigation does not bring it back, a reload or a new tab does. Running from
 * the Home Screen or the Dock is what makes it stop, because `detect.ts` then
 * finds nothing to warn about.
 *
 * The same native `<dialog>` as the first-run guide, for the same reasons: the
 * top layer, inertness, focus trapping and Escape without a dependency.
 *
 * `showBackup` is false on a first run. There is nothing stored to back up or
 * carry across yet — that is the best moment to move to the Home Screen — and
 * the first-run guide follows this notice, so a Back up link here would open
 * Settings only for the guide to land on top of it. On every later launch the
 * backup half is here instead, and the guide is not.
 */
export function StorageNoticeDialog({
  risk,
  showBackup,
  onClose,
}: {
  risk: StorageRisk;
  showBackup: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog !== null && !dialog.open) {
      dialog.showModal();
    }
  }, []);

  const close = () => {
    ref.current?.close();
  };
  const copy = COPY[risk];

  return (
    <dialog
      ref={ref}
      aria-labelledby="storage-notice-title"
      onClose={onClose}
      className="border-border bg-surface text-foreground rounded-container backdrop:bg-foreground/40 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto border p-0 shadow-lg print:hidden"
    >
      <div className="p-5 sm:p-6">
        <h2 id="storage-notice-title" className="text-base font-semibold tracking-tight">
          Browser storage
        </h2>
        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
          {copy.cause} This includes flights and logbook entries.
        </p>

        <h3 className="mt-4 text-sm font-medium">Keep data</h3>
        <p className="text-muted-foreground mt-0.5 text-sm leading-relaxed">{copy.fix}</p>
        <p className="mt-1 text-sm">{copy.how}</p>

        {showBackup ? (
          <>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{copy.move}</p>
            <h3 className="mt-4 text-sm font-medium">Backup</h3>
            <p className="text-muted-foreground mt-0.5 text-sm leading-relaxed">
              Back up regularly. A backup file is the only copy outside this browser.
            </p>
          </>
        ) : null}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant={showBackup ? 'ghost' : 'primary'} onClick={close}>
            Close
          </Button>
          {showBackup ? (
            <Button asChild>
              <Link {...BACKUP_LINK} onClick={close}>
                Back up
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </dialog>
  );
}
